import { type Dispatch, type SetStateAction } from 'react';
import { length as turfLength, lineString as turfLineString } from '@turf/turf';
import { type LngLat, type LngLatBounds } from '@yandex/ymaps3-types';
import { fetchRoute } from '@/entities/route';
import { useAsyncFn } from '@/shared/lib/hooks';
import { DEFAULT_ZOOM } from '@/shared/lib/yandexMap';

const MAX_ROUTE_LENGTH = 3000;

interface UseRouteProps {
    pickupPointId?: string;
    currentPositionCoords?: GeolocationCoordinates | null;
    setCoordinates: Dispatch<SetStateAction<LngLat | null>>;
    setZoom: Dispatch<SetStateAction<number | undefined>>;
    setRouteBounds: Dispatch<SetStateAction<LngLatBounds | null>>;
}

export const useRoute = (props: UseRouteProps) => {
    const { pickupPointId, currentPositionCoords, setCoordinates, setZoom, setRouteBounds } = props;
    const [{ value: route }, loadRoute] = useAsyncFn(
        async (pickupPointId?: string, pickupPointPosition?: { longitude: number; latitude: number }) => {
            if (currentPositionCoords && pickupPointId && pickupPointPosition) {
                return await fetchRoute(
                    [currentPositionCoords.longitude, currentPositionCoords.latitude],
                    [pickupPointPosition.longitude, pickupPointPosition.latitude]
                ).then((route) => {
                    if (!route) {
                        setCoordinates([pickupPointPosition.longitude, pickupPointPosition.latitude]);
                        setZoom(DEFAULT_ZOOM);
                        setRouteBounds(null);

                        return null;
                    }

                    const line = turfLineString(
                        route.geometry.coordinates.map((coordinate) => [coordinate[0], coordinate[1]])
                    );
                    const length = turfLength(line, { units: 'meters' });

                    if (length < MAX_ROUTE_LENGTH && route?.properties.bounds) {
                        setRouteBounds(route.properties.bounds);

                        return route;
                    }

                    setCoordinates([pickupPointPosition.longitude, pickupPointPosition.latitude]);
                    setZoom(DEFAULT_ZOOM);
                    setRouteBounds(null);

                    return null;
                });
            }

            return null;
        },
        [currentPositionCoords, pickupPointId, setCoordinates, setZoom, setRouteBounds]
    );

    return { route, loadRoute };
};
